import * as React from 'react';
import styled from 'styled-components';
import { NavLink } from 'react-router-dom';
import { BREAKPOINTS, COLORS } from '../styles';
import { ChevronLeft, ChevronRight, MoreHorizontal } from '../icons';

const Container = styled.nav`
  margin: 20px 0;
  display: flex;
  justify-content: center;

  ${BREAKPOINTS.MEDIUM} {
    width: 75%;
  }
`;

const Items = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
`;

const Item = styled.li`
  width: 48px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 19px;
  line-height: 23px;
  font-weight: 400;

  & > * {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    text-decoration: none;
    font-family: 'Lato', sans-serif;
    color: ${COLORS.neutral_600};
    border-bottom: 3px solid transparent;
    &:focus,
    &:focus-within {
      box-shadow: 0px 4px 12px rgba(46, 127, 161, 0.25), 0px 0px 8px #2e7fa1;
      outline-width: 0;
    }
  }
  & > a:hover {
    color: ${COLORS.primary_800};
    background-color: ${COLORS.primary_20};
    border-bottom: 3px solid transparent;
    box-shadow: none;
  }
  & > a:active {
    color: ${COLORS.primary_700};
    background-color: ${COLORS.primary_100};
    border-bottom: 3px solid transparent;
    box-shadow: none;
  }

  & > a.active {
    color: ${COLORS.neutral_800};
    border-bottom: 3px solid ${COLORS.primary_500};
    background-color: ${COLORS.neutral_20};
    cursor: default;
    box-shadow: none;
    &:hover {
      color: ${COLORS.primary_800};
      background-color: ${COLORS.primary_20};
      border-bottom: 3px solid ${COLORS.primary_500};
    }
    &:active {
      color: ${COLORS.primary_700};
      background-color: ${COLORS.primary_100};
      border-bottom: 3px solid ${COLORS.primary_500};
    }
  }
`;

const Dots = styled(Item)`
  padding-top: 1%;
  cursor: not-allowed;
`;

type PaginatorProps = {
  pageCount: number;
  currentPage: number;
  baseUrl: string;
  onPageChange?: (page: number) => void;
};

const Step = ({
  up = true,
  target = '',
  disabled = false,
  page,
  onPageChange,
  testId,
}: {
  up: boolean;
  target: string;
  disabled: boolean;
  page: number;
  onPageChange?: (page: number) => void;
  testId?: string;
}) => {
  if (disabled)
    return (
      <Item style={{ cursor: 'not-allowed' }} data-testid={`${testId}_Disabled`}>
        {up ? <ChevronRight size="20" color={COLORS.neutral_300} /> : <ChevronLeft size="20" color={COLORS.neutral_300} />}
      </Item>
    );
  return (
    <Item data-testid={`${testId}_Enabled`}>
      <NavLink
        to={target}
        onClick={() => {
          if (onPageChange) onPageChange(page);
        }}>
        {up ? <ChevronRight size="20" /> : <ChevronLeft />}
      </NavLink>
    </Item>
  );
};

const Paginator = ({ pageCount = 1, currentPage = 1, baseUrl = '', onPageChange }: PaginatorProps) => {
  const pages = [];
  let from = 2;
  let to = 4;
  if (pageCount === 0) return null;

  if (pageCount > 1) {
    if (currentPage >= 4 && currentPage <= pageCount - 2) {
      from = currentPage - 1;
      to = currentPage + 1;
    } else if (pageCount - 2 > 1 && currentPage > pageCount - 2) {
      from = pageCount - 3;
      to = pageCount - 1;
    }
    if (to > pageCount) {
      to = pageCount;
    }
    for (let i = from; i <= to; i += 1) {
      pages.push(i);
    }
  }

  return (
    <Container>
      <Items>
        <Step
          up={false}
          target={`${baseUrl}/${currentPage - 1}`}
          page={currentPage - 1}
          disabled={currentPage === 1}
          onPageChange={() => {
            if (onPageChange) onPageChange(currentPage - 1);
          }}
          testId="Test-StepLeft"
        />
        {pages.findIndex(item => item === 1) === -1 && (
          <Item>
            <NavLink
              to={`${baseUrl}/1`}
              onClick={() => {
                if (onPageChange) onPageChange(1);
              }}>
              1
            </NavLink>
          </Item>
        )}
        {currentPage >= 4 && pageCount > 5 && pageCount && pages.findIndex(item => item === 1) === -1 && (
          <Dots>
            <MoreHorizontal color={COLORS.neutral_600} />
          </Dots>
        )}
        {pages.map(page => (
          <Item key={page}>
            <NavLink
              to={`${baseUrl}/${page}`}
              onClick={() => {
                if (onPageChange) onPageChange(page);
              }}>
              {page}
            </NavLink>
          </Item>
        ))}
        {pageCount > 5 && currentPage <= pageCount - 3 && (
          <Dots>
            <MoreHorizontal color={COLORS.neutral_600} />
          </Dots>
        )}
        {pages.findIndex(item => item === pageCount) === -1 && pageCount !== 1 && (
          <Item>
            <NavLink
              to={`${baseUrl}/${pageCount}`}
              onClick={() => {
                if (onPageChange) onPageChange(pageCount);
              }}>
              {pageCount}
            </NavLink>
          </Item>
        )}
        <Step
          up
          target={`${baseUrl}/${currentPage + 1}`}
          page={currentPage + 1}
          disabled={currentPage === pageCount}
          onPageChange={() => {
            if (onPageChange) onPageChange(currentPage + 1);
          }}
          testId="Test-StepRight"
        />
      </Items>
    </Container>
  );
};

export default Paginator;
